import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { DG } from '@cotera/sdk/core';
import { JsonTypeBuilderNodeView } from './view';
import type { ExtensionMenuConfig } from '@cotera/client/app/components/app/rte/types/extension-config';
import type { SlashCommand } from '@cotera/client/app/components/app/rte/types/extension-interface';

type JSONNode = DG.JSONNode;

export interface JsonTypeBuilderOptions extends ExtensionMenuConfig {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    jsonTypeBuilder: {
      /**
       * Insert a JSON type builder block
       */
      setJsonTypeBuilder: (attributes?: { jsonNode?: JSONNode }) => ReturnType;
    };
  }
}

export const JsonTypeBuilderNode = Node.create<JsonTypeBuilderOptions>({
  name: 'response-shape',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: 'block',

  atom: true,
  selectable: false,

  addAttributes() {
    return {
      jsonNode: {
        default: {
          t: 'string',
          nullable: false,
        } as JSONNode,
        parseHTML: (element) => {
          const data = element.getAttribute('data-json-node');
          if (data) {
            try {
              return JSON.parse(data);
            } catch {
              return {
                t: 'string',
                nullable: false,
              };
            }
          }
          return {
            t: 'string',
            nullable: false,
          };
        },
        renderHTML: (attributes) => {
          return {
            'data-json-node': JSON.stringify(attributes['jsonNode']),
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="json-type-builder"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'json-type-builder' }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(JsonTypeBuilderNodeView);
  },

  addCommands() {
    return {
      setJsonTypeBuilder:
        (attributes = {}) =>
        ({ commands }) => {
          // Insert the node and then move cursor after it to avoid selection highlighting
          commands.insertContent(
            {
              type: this.name,
              attrs: attributes,
            },
            {
              updateSelection: false, // <-- This prevents auto-selection
            }
          );

          // Move cursor to after the node to prevent it from being selected
          return commands.setTextSelection(-1);
        },
    };
  },

  // Add turn-into and slash command configurations
  addStorage() {
    return {
      getSlashConfig: (): SlashCommand => ({
        title: 'Response Shape',
        subtext: 'Instruct the agent to return a specific response shape',
        keywords: [
          'response',
          'shape',
          'json',
          'schema',
          'type',
          'builder',
          'structure',
        ],
        badge: 'code-block',
        group: 'Insert',
        available: (editor) => {
          // Basic checks
          if (!editor.isEditable || !editor.can().setJsonTypeBuilder()) {
            return false;
          }

          // Check if there's already a response-shape node in the document
          let hasResponseShape = false;
          editor.state.doc.descendants((node) => {
            if (node.type.name === 'response-shape') {
              hasResponseShape = true;
              return false; // Stop traversing
            }
            return true; // Continue traversing
          });

          // Only available if there's no response-shape in the document
          return !hasResponseShape;
        },
        action: ({ editor }) => {
          editor.chain().focus().setJsonTypeBuilder().run();
        },
      }),
    };
  },
});
